/**
 * PlanCard — what the run is about to do, stated before it does it.
 *
 * Two lives, one component:
 *
 *   `muted`  — a quiet strip under a clarifying question or above an answer.
 *              Read-only. It is shown on ORDINARY turns too, deliberately: a
 *              plan the user only sees when we are unsure is a plan they
 *              cannot trust when we are sure.
 *   editable — the CONFIRM tier. The plan card the gate renders before an
 *              expensive run, with the run already under way.
 *
 * THE CONFIRM TIER AUTO-PROCEEDS. Confirmation is a keystroke, not a gate: the
 * backend has already enqueued the turn, and this card exists so the user can
 * see and correct the assumptions, not so they can grant permission. Editing
 * re-decides and re-runs; doing nothing runs.
 *
 * The ASSUMPTIONS list is the point of the whole card — every slot filled by a
 * default rather than by the user, named out loud. "Norway, from your account
 * settings" is a sentence the user can act on; a silent jurisdiction is not.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiInfoLight, PiPencilSimpleLight } from 'react-icons/pi';
import { ResearchPlan } from '../clarify';

interface Props {
  plan: ResearchPlan;
  /** Read-only strip. The default; editing is the confirm tier only. */
  muted?: boolean;
  /**
   * Change the plan and re-run the SAME turn. Present only on the confirm
   * tier; its absence is what makes the card read-only.
   */
  onEdit?: (answers: Record<string, string>) => void;
  /** The run is under way — the card is a statement, not a prompt. */
  running?: boolean;
}

const FORMATS = ['answer', 'memo', 'bullets', 'timeline', 'overview',
                 'source_list', 'find_document', 'draft'];

/** The plan in one line: shape, then scope. */
export function planSummary(plan: ResearchPlan, t: (key: string) => string): string {
  const parts: string[] = [];
  if (plan.format) parts.push(t(plan.format));
  if (plan.uploads_only) {
    parts.push(t('your attachments'));
  } else if (plan.jurisdictions?.length) {
    parts.push(plan.jurisdictions.join(', '));
  }
  return parts.join(' · ');
}

const PlanCard: React.FC<Props> = ({ plan, muted = false, onEdit, running }) => {
  const { t } = useTranslation();
  // AUTO-PROCEED, LATCHED. Once the user reaches for the controls the card
  // stops behaving like a passing statement; it never re-arms, because a card
  // that quietly reverts to "proceeding" under someone's cursor is worse than
  // one that stays open.
  const [editing, setEditing] = useState(false);
  const assumptions = plan?.assumptions || [];

  if (!plan || (!plan.format && !assumptions.length)) return null;

  const summary = planSummary(plan, t);

  return (
    <div
      className={clsx(
        'rounded-lg border text-xs',
        muted
          ? 'border-hairline-soft bg-surface-sunken/60 px-2.5 py-2'
          : 'glass border-hairline-soft p-3'
      )}
      data-testid="plan-card"
      data-action={plan.action || ''}
      data-reason={plan.reason || ''}
    >
      <div className="flex items-start gap-2">
        <PiInfoLight
          className="w-3.5 h-3.5 mt-px text-fg-muted flex-shrink-0"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <p className="text-fg-body" data-testid="plan-summary">
            {running ? t('Searching') : t('Plan')}
            {summary ? ` — ${summary}` : ''}
          </p>

          {assumptions.length > 0 && (
            <ul className="mt-1 space-y-0.5" data-testid="plan-assumptions">
              {assumptions.map((assumption) => (
                <li key={assumption} className="text-fg-muted">
                  {t(assumption)}
                </li>
              ))}
            </ul>
          )}

          {onEdit && !editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid="plan-edit"
              className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                         text-[11px] text-fg-muted hover:text-fg-strong
                         transition-colors duration-200 ease-slide"
            >
              <PiPencilSimpleLight className="w-3 h-3" aria-hidden />
              {t('Change')}
            </button>
          )}

          {onEdit && editing && (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap"
                 data-testid="plan-editor">
              {FORMATS.map((format) => (
                <button
                  key={format}
                  type="button"
                  onClick={() => { setEditing(false); onEdit({ format }); }}
                  data-testid={`plan-format-${format}`}
                  aria-pressed={plan.format === format}
                  className={clsx(
                    'px-2 py-0.5 rounded-full text-[11px] border',
                    'transition-colors duration-200 ease-slide',
                    plan.format === format
                      ? 'bg-champagne/20 border-champagne/60 text-fg-strong'
                      : 'bg-surface-sunken border-hairline-soft text-fg-body hover:border-champagne/40'
                  )}
                >
                  {t(format)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanCard;
